import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { BlackoutCoverageSummary } from '../tabs/blackoutDates/types';
import type { DealOptionRowView } from '../tabs/dealOptionsInclusions/types';
import type { UnlinkedDealOptionView } from '../tabs/rooms/types';
import type { ChecklistCategoryId, ChecklistRowsByCategory } from '../types';

import { presentBlackoutRows } from './blackout/presentBlackoutRows';
import { presentBundledExperienceRows } from './bundledExperience/presentBundledExperienceRows';
import { presentCampaignRows } from './campaign/presentCampaignRows';
import { presentDealOptionsInclusionsRows } from './dealOptionsInclusions/presentDealOptionsInclusionsRows';
import { presentDiscountsMarginRows } from './discountsMargin/presentDiscountsMarginRows';
import { presentEgsRows } from './egs/presentEgsRows';
import { presentRoomRows } from './rooms/presentRoomRows';
import { presentSetupRows } from './setup/presentSetupRows';
import { presentSurchargesRows } from './surcharges/presentSurchargesRows';
import { presentTaxesPaymentsRows } from './taxesPayments/presentTaxesPaymentsRows';

export interface PresentedChecklist extends ChecklistRowsByCategory {
  blackoutCoverage: BlackoutCoverageSummary;
  categorySyncedAt: Partial<Record<ChecklistCategoryId, string>>;
  dealOptionsInclusionsDealRows: Array<DealOptionRowView>;
  opportunityTravelFrom?: string;
  opportunityTravelTo?: string;
  unlinkedDealOptions: Array<UnlinkedDealOptionView>;
}

interface RowsByKind {
  blackout_coverage: Array<CmChecklist.BlackoutCoverageRow>;
  blackout_date: Array<CmChecklist.BlackoutDateRow>;
  bundled_experience: Array<CmChecklist.BundledExperienceRow>;
  campaign_check: Array<CmChecklist.CampaignRow>;
  deal_option_approval: Array<CmChecklist.DealOptionApprovalRow>;
  deal_option_inclusion: Array<CmChecklist.DealOptionInclusionRow>;
  discounts_margin_check: Array<CmChecklist.DiscountsMarginRow>;
  egs_age_coverage: Array<CmChecklist.EgsAgeCoverageRow>;
  egs_capacity: Array<CmChecklist.EgsCapacityRow>;
  egs_margin: Array<CmChecklist.EgsMarginRow>;
  room: Array<CmChecklist.RoomRow>;
  setup_check: Array<CmChecklist.SetupRow>;
  surcharge_check: Array<CmChecklist.SurchargeCheckRow>;
  surcharge_festive_coverage: Array<CmChecklist.SurchargeFestiveCoverageRow>;
  surcharge_period: Array<CmChecklist.SurchargePeriodRow>;
  taxes_payments_check: Array<CmChecklist.TaxesPaymentsRow>;
}

// Maps the evaluation onto the row shapes the report cards already render. Rows of a kind this
// build does not know are dropped with a warning rather than crashing the page.
export function presentChecklist(evaluation: CmChecklist.Evaluation): PresentedChecklist {
  const byKind = groupRowsByKind(evaluation.rows);
  const blackout = presentBlackoutRows({ coverageRow: byKind.blackout_coverage[0], dateRows: byKind.blackout_date });
  const dealOptions = presentDealOptionsInclusionsRows({
    approvalRows: byKind.deal_option_approval,
    inclusionRows: byKind.deal_option_inclusion,
  });
  const egs = presentEgsRows({
    ageCoverageRows: byKind.egs_age_coverage,
    capacityRows: byKind.egs_capacity,
    marginRows: byKind.egs_margin,
  });
  const rooms = presentRoomRows({ approvalRows: byKind.deal_option_approval, roomRows: byKind.room });
  const surcharges = presentSurchargesRows({
    checkRows: byKind.surcharge_check,
    festiveCoverageRows: byKind.surcharge_festive_coverage,
    periodRows: byKind.surcharge_period,
  });

  return {
    baseSurchargesRows: surcharges.base,
    blackoutCoverage: blackout.coverage,
    blackoutRows: blackout.rows,
    bundledExperienceRows: presentBundledExperienceRows(byKind.bundled_experience),
    campaignRows: presentCampaignRows(byKind.campaign_check),
    capacityRows: egs.capacity,
    categorySyncedAt: Object.fromEntries(
      evaluation.categories
        .filter((category) => category.synced_at)
        .map((category) => [category.id, category.synced_at]),
    ),
    dealOptionApprovalRows: dealOptions.dealOptionApprovalRows,
    dealOptionsInclusionsDealRows: dealOptions.dealRows,
    dealOptionsInclusionsRows: dealOptions.inclusionRows,
    discountsMarginRows: presentDiscountsMarginRows(byKind.discounts_margin_check),
    egsAgeCoverageRows: egs.ageCoverage,
    marginRows: egs.margin,
    opportunityTravelFrom: blackout.opportunityTravelFrom,
    opportunityTravelTo: blackout.opportunityTravelTo,
    roomRows: rooms.rows,
    setupRows: presentSetupRows(byKind.setup_check),
    surchargeTableRows: surcharges.table,
    taxesPaymentsRows: presentTaxesPaymentsRows(byKind.taxes_payments_check),
    unlinkedDealOptions: rooms.unlinkedDealOptions,
  };
}

function groupRowsByKind(rows: Array<CmChecklist.Row>): RowsByKind {
  const byKind: RowsByKind = {
    blackout_coverage: [],
    blackout_date: [],
    bundled_experience: [],
    campaign_check: [],
    deal_option_approval: [],
    deal_option_inclusion: [],
    discounts_margin_check: [],
    egs_age_coverage: [],
    egs_capacity: [],
    egs_margin: [],
    room: [],
    setup_check: [],
    surcharge_check: [],
    surcharge_festive_coverage: [],
    surcharge_period: [],
    taxes_payments_check: [],
  };

  for (const row of rows) {
    const bucket = byKind[row.kind] as Array<CmChecklist.Row> | undefined;

    if (!bucket) {
      console.warn(`CM checklist: dropping row of unknown kind "${row.kind}" (${row.item_key})`);
      continue;
    }

    bucket.push(row);
  }

  return byKind;
}
